import React, {
  FC, useEffect, useMemo, useRef, useState,
} from 'react'

import { ArrowRepeat, BoundingBox, CheckCircle, EyeFill } from 'react-bootstrap-icons'
import { GetModelActions, GetModelProps } from './GetModel.types'
import { useGetHbjson, useSendHbjson } from '../hooks'

import { SettingsButton } from '../atoms/SettingsButton'
import FileInput from '../atoms/FileInput/FileInput'
import { useCustomEvent } from '../hooks/useCustomEvent'

export const GetModel: FC<GetModelProps> = ({
  onChange,
  optionsConfig,
  buttonLabel='Get Model',
  showHelpText=true,
  defaultKey='get-model-btn',
  useIcon=true,
  fullWidth=false
}) => {
  const { sendHbjson } = useSendHbjson()

  const [isClicked, setIsClicked] = useState<boolean>(false)

  const [selection, setSelection] = useState<boolean>()
  const [subscribe, setSubscribe] = useState<boolean>()
  const [preview, setPreview] = useState<boolean>()

  const [refresh, setRefresh] = useState<string>()
  const [result, setResult] = useState<string>()

  // This method does not read runtime state variables. Props only
  const customEventHandler = (e: CustomEvent) => {
    (e.detail.id === defaultKey) && setRefresh(performance.now().toString())
  }
  useCustomEvent('onModelChanged', customEventHandler)

  // Config only on bootstrap
  const initialConfig = useMemo(() : { [index in GetModelActions] : { show?: boolean, selected?: boolean }} => {
    if (!optionsConfig) return undefined
    return optionsConfig
  }, [])

  // pollination-react-io hooks
  const { host, hbjson, getHbjson } = useGetHbjson()

  // set selection and subscribe on props change
  useEffect(() => {
    if(!initialConfig) return

    const sel = Boolean(initialConfig) && typeof initialConfig !== 'undefined' && 
      typeof initialConfig.selection !== 'undefined' && 
      typeof initialConfig.selection.selected !== 'undefined' ? initialConfig.selection.selected : false

    const sub = Boolean(initialConfig) && typeof initialConfig !== 'undefined' && 
      typeof initialConfig.subscribe !== 'undefined' && 
      typeof initialConfig.subscribe.selected !== 'undefined' ? initialConfig.subscribe.selected : false

    // Do not trigger prev props - for streamlit

    setSelection(sel)
    setSubscribe(sub)
  }, [initialConfig])

  /**
   * From Blob geometry to string
   */
  useEffect(() => {
    // Only if clicked
    if (!hbjson || !isClicked) return
    const fr = new FileReader()
    fr.onload = function (this: FileReader) {
      if (typeof this.result !== 'string') return
      setResult(this.result)
    }
    fr.readAsText(hbjson)
  }, [hbjson])

  /**
   * Set parent state if result change
   */
  useEffect(() => {
    if (!result || !onChange) return
    onChange(new File([result], `Model_linked_from_${host}.hbjson`))
  }, [result, onChange])

  /**
   * Preview only
   */
  useEffect(() => {
    if (!result) return
    // Preview ON
    preview && sendHbjson(
      'SubscribePreviewHBJSON',
      {
        data: result,
        uniqueId: defaultKey,
        options: {}
      },
    )
    // Preview OFF
    !preview && sendHbjson(
      'ClearHBJSON',
      {
        data: result,
        uniqueId: defaultKey,
        options: {}
      },
    )
  }, [result, preview])

  // Event effect if subscribe is active
  // If subscribe is enable or disable rerun
  useEffect(() => {
    if (!getHbjson) return
    subscribe && getHbjson(defaultKey, false, false, selection)
  }, [getHbjson, refresh, subscribe])

  return (
    host === 'web'
      ? (
        <FileInput
          onChange={(file) => {
            if (!file) return
            onChange && onChange(file)
          }}
        />
      )
      : (
        <SettingsButton
          containerStyle={{
            display: 'flex',
            width: fullWidth ? '100%' : undefined,
          }}
          mainButtonStyle={{
            width: '100%',
            fontSize: '12px'
          }}
          onClick={() => {
            if (!getHbjson) return
            setIsClicked(true)
            getHbjson(defaultKey, selection, true, selection)
          }}
          disabled={!getHbjson}
          // @ts-ignore
          options={host !== 'revit' ? [{
            options: [
              {
                type: 'checkbox',
                id: GetModelActions.subscribe,
                label: 'Auto Reload',
                disabled: !getHbjson,
                checked: subscribe,
                icon: <ArrowRepeat size={16} />,
                onSelect: () => {
                  setSubscribe((state) => !state)
                },
              },
              {
                type: 'checkbox',
                id: GetModelActions.selection,
                label: 'Select',
                disabled: !getHbjson,
                checked: selection,
                icon: <BoundingBox size={16} />,
                onSelect: () => {
                  setSelection((state) => !state)
                },
              },
              {
                type: 'checkbox',
                id: GetModelActions.preview,
                label: 'Preview',
                disabled: !getHbjson,
                checked: preview,
                icon: <EyeFill size={16} />,
                onSelect: () => {
                  setPreview((state) => !state)
                },
              },
            ].filter(o => {
              if (!initialConfig) return true
              return typeof initialConfig[o.id] === 'undefined' ? true : 
                typeof initialConfig[o.id].show === 'undefined' ? true : initialConfig[o.id].show
            }),
          }] : undefined}
          asButtons={true}
          label={buttonLabel}
          helpText={showHelpText && `Get ${selection ? 'selected ' : ''} model${subscribe ? ' continuously' : ''}.`}
          icon={useIcon && ((isClicked && hbjson) ? <CheckCircle/> : undefined)}
        />
      )
  )
}

export default GetModel